import dayjs from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';

dayjs.extend(quarterOfYear);

export const MEASURE_KEYS = {
  TOTAL_QTY: 'totalQty',
  TOTAL_AMOUNT: 'totalAmount',
  ORDER_COUNT: 'orderCount'
};

export const DIMENSION_FIELDS = [
  { key: 'customerName', labelKey: 'reports.commerce.customers', isDate: false },
  { key: 'customerNo', labelKey: 'commerceCustomers.customerNo', isDate: false },
  { key: 'paymentTerm', labelKey: 'reports.commerce.paymentTerm', isDate: false },
  { key: 'status', labelKey: 'commerceSalesOrders.status', isDate: false },
  { key: 'source', labelKey: 'commerceSalesOrders.source', isDate: false },
  { key: 'currency', labelKey: 'commerceSalesOrders.currency', isDate: false },
  { key: 'salesmanName', labelKey: 'commerceSalesOrders.salesmanName', isDate: false },
  { key: 'deliveryDate', labelKey: 'reports.commerce.deliveryDate', isDate: true },
  { key: 'orderDate', labelKey: 'commerceSalesOrders.orderDate', isDate: true }
];

export const MEASURE_OPTIONS = [
  { key: MEASURE_KEYS.TOTAL_QTY, labelKey: 'reports.commerce.qty' },
  { key: MEASURE_KEYS.TOTAL_AMOUNT, labelKey: 'reports.commerce.total' },
  { key: MEASURE_KEYS.ORDER_COUNT, labelKey: 'reports.commerce.orderCount' }
];

const DATE_GRANULARITIES = [
  { value: 'year', labelKey: 'reports.pivotDateYear' },
  { value: 'quarter', labelKey: 'reports.pivotDateQuarter' },
  { value: 'month', labelKey: 'reports.pivotDateMonth' },
  { value: 'day', labelKey: 'reports.pivotDateDay' }
];

export { DATE_GRANULARITIES };

function dateGroupKey(raw, granularity) {
  const d = raw ? dayjs(raw) : null;
  if (!d || !d.isValid()) return '—';
  switch (granularity) {
    case 'year': return d.format('YYYY');
    case 'quarter': return `Q${d.quarter()} ${d.format('YYYY')}`;
    case 'month': return d.format('MMMM YYYY');
    case 'day': return d.format('YYYY-MM-DD');
    default: return d.format('MMMM YYYY');
  }
}

function parseDateKeyForSort(key) {
  if (!key || key === '—') return Infinity;
  const monthNames = {
    January: 1, February: 2, March: 3, April: 4, May: 5, June: 6,
    July: 7, August: 8, September: 9, October: 10, November: 11, December: 12
  };
  const monthMatch = key.match(/^(\w+)\s+(\d{4})$/);
  if (monthMatch) {
    const m = monthNames[monthMatch[1]] || 0;
    return parseInt(monthMatch[2], 10) * 100 + m;
  }
  const qMatch = key.match(/^Q(\d)\s+(\d{4})$/);
  if (qMatch) return parseInt(qMatch[2], 10) * 10 + parseInt(qMatch[1], 10);
  if (/^\d{4}$/.test(key)) return parseInt(key, 10) * 100;
  if (/^\d{4}-\d{2}-\d{2}$/.test(key)) return dayjs(key).valueOf();
  return Infinity;
}

function getDimensionValue(record, dimension) {
  if (!dimension) return 'Total';
  const raw = record[dimension.fieldId];
  if (dimension.isDate && dimension.dateGranularity) {
    return dateGroupKey(raw, dimension.dateGranularity);
  }
  return raw != null && raw !== '' ? String(raw) : '—';
}

function getMeasureValue(record, measureKey) {
  switch (measureKey) {
    case MEASURE_KEYS.TOTAL_QTY:
      return Number(record.totalQty) || 0;
    case MEASURE_KEYS.TOTAL_AMOUNT:
      return Number(record.totalAmount) || 0;
    case MEASURE_KEYS.ORDER_COUNT:
      return 1;
    default:
      return 0;
  }
}

export function computeCommercePivot(dataSource, rowDimension, colDimension, measureKeys) {
  const measures = measureKeys?.length ? measureKeys : [MEASURE_KEYS.TOTAL_AMOUNT];
  const rowKeys = new Set();
  const colKeys = new Set();
  const cells = {};

  (dataSource || []).forEach((record) => {
    const rk = getDimensionValue(record, rowDimension);
    const ck = getDimensionValue(record, colDimension);
    rowKeys.add(rk);
    colKeys.add(ck);
    if (!cells[rk]) cells[rk] = {};
    if (!cells[rk][ck]) {
      cells[rk][ck] = {};
      measures.forEach((mk) => {
        cells[rk][ck][mk] = { value: 0, orderIds: [] };
      });
    }
    measures.forEach((mk) => {
      cells[rk][ck][mk].value += getMeasureValue(record, mk);
      if (record.orderId) cells[rk][ck][mk].orderIds.push(record.orderId);
    });
  });

  const rowIsDate = rowDimension?.isDate && rowDimension?.dateGranularity;
  const colIsDate = colDimension?.isDate && colDimension?.dateGranularity;
  const sortFn = (isDate) => (a, b) => {
    if (a === 'Total') return -1;
    if (b === 'Total') return 1;
    if (a === '—') return 1;
    if (b === '—') return -1;
    if (isDate) return parseDateKeyForSort(a) - parseDateKeyForSort(b);
    return a.localeCompare(b);
  };

  const rows = Array.from(rowKeys).sort(sortFn(rowIsDate));
  const cols = Array.from(colKeys).sort(sortFn(colIsDate));
  return { rows, cols, cells, measures };
}

export function computeCommerceTotals(rows, cols, cells, measures) {
  const rowTotals = {};
  const colTotals = {};
  const grandTotal = {};
  measures.forEach((mk) => { grandTotal[mk] = { value: 0, orderIds: [] }; });

  rows.forEach((rk) => {
    rowTotals[rk] = {};
    measures.forEach((mk) => { rowTotals[rk][mk] = { value: 0, orderIds: [] }; });
    cols.forEach((ck) => {
      measures.forEach((mk) => {
        const cell = cells[rk]?.[ck]?.[mk];
        if (cell) {
          rowTotals[rk][mk].value += cell.value;
          rowTotals[rk][mk].orderIds.push(...cell.orderIds);
        }
      });
    });
  });

  cols.forEach((ck) => {
    colTotals[ck] = {};
    measures.forEach((mk) => { colTotals[ck][mk] = { value: 0, orderIds: [] }; });
    rows.forEach((rk) => {
      measures.forEach((mk) => {
        const cell = cells[rk]?.[ck]?.[mk];
        if (cell) {
          colTotals[ck][mk].value += cell.value;
          colTotals[ck][mk].orderIds.push(...cell.orderIds);
        }
      });
    });
  });

  rows.forEach((rk) => {
    measures.forEach((mk) => {
      grandTotal[mk].value += rowTotals[rk][mk].value;
      grandTotal[mk].orderIds.push(...rowTotals[rk][mk].orderIds);
    });
  });

  return { rowTotals, colTotals, grandTotal };
}
